/**
 * SupplierDetailPage Component
 * 협력사 상세 정보 관리
 */
import React, {useCallback, useEffect, useState} from "react";
import { SupplierInfo } from "../types/supplierInfo.types";
import { DeductDto } from "../types/supplier.api.types";
import { supplierInfoFromDto } from "../utils/supplierHelpers";
import {getItem} from "@/shared/utils/storage";
import {useToast} from "@/shared/components/feedback/Toast/ToastContext";
import {ToastType} from "@/shared/components/feedback/Toast/Toast.types";
import { supplierApi } from "../api/supplierApi";
import {usePagination} from "@/shared/hooks/usePagination";
import {useSupplierDetailHandlers} from "../hooks/useSupplierDetailHandlers";
import { ITEMS_PER_PAGE } from '../constants/supplierStatus.constants';
import Loading from '@/shared/components/feedback/Loading';
import BasicInfoSection from './BasicInfoSection';
import SettlementInfoSection from './SettlementInfoSection';
import DepositInfoSection from './DepositInfoSection';
import BusinessInfoSection from './BusinessInfoSection';
import DeductionSection from './DeductionSection';
import "@/shared/styles/common-table.css";
import "../styles/SupplierManagement.css";
import "../styles/SupplierDetailPage.css";

interface SupplierDetailProps {
    selectedSupplierId?: string;
    supplierName?: string;
    onBack?: () => void;
    onUnsavedChanges?: (hasChanges: boolean) => void;
}

const SupplierDetailPage: React.FC<SupplierDetailProps> = ({ selectedSupplierId, supplierName, onBack, onUnsavedChanges }) => {
    // ========================================
    // 1. Hooks - Toast
    // ========================================
    const { showToast } = useToast();

    // ========================================
    // 2. State - 로컬 상태 관리
    // ========================================
    const [supplierInfo, setSupplierInfo] = useState<SupplierInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [deductions, setDeductions] = useState<DeductDto[]>([]);
    const [isEdited, setIsEdited] = useState(false);

    // ========================================
    // 3. Hooks - 페이지네이션 (일차감 목록)
    // ========================================
    const {
        currentPage,
        totalPages,
        currentItems: currentDeductions,
        goToPage
    } = usePagination({ items: deductions, itemsPerPage: ITEMS_PER_PAGE.DEFAULT });

    // ========================================
    // 4. Hooks - 핸들러 함수
    // ========================================
    const {
        saving,
        handleSaveSettlementInfo,
        handleSaveBusinessInfo,
        handleSaveBasicInfo,
        handleFieldChange
    } = useSupplierDetailHandlers({
        supplierInfo,
        setSupplierInfo
    });

    // ========================================
    // 5. API 함수 - 데이터 로드
    // ========================================
    /**
     * 협력사 정보 및 일차감 목록 조회
     * 본사 사용자: selectedSupplierId로 조회
     * 협력사 사용자: 자신의 userId로 조회
     */
    const fetchSupplierInfo = useCallback(async () => {
        try {
            setLoading(true);
            setError(false);

            // localStorage에서 사용자 정보 가져오기
            const userId = getItem<string>('USER_ID');
            const userTypeValue = getItem<string>('USER_TYPE');

            // 조회할 협력사 ID 결정
            const targetSupplierId = userTypeValue === 'HEADQUARTERS' ? selectedSupplierId : userId;

            if (!targetSupplierId) {
                setLoading(false);
                return;
            }

            // 협력사 정보 조회
            const response = await supplierApi.getSupplierInfo(targetSupplierId);
            setSupplierInfo(supplierInfoFromDto(response.supplier));

            // 일차감 정보 조회 (선택한 협력사의 일차감만 조회)
            try {
                const deductResponse = await supplierApi.getDeductList(targetSupplierId);
                setDeductions(deductResponse.deducts);
            } catch {
                // 일차감 정보 조회 실패는 무시 (선택적 데이터)
            }

            setLoading(false);

        } catch {
            setError(true);
            showToast('협력사 정보를 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setLoading(false);
        }
    }, [showToast, selectedSupplierId]);

    /**
     * 재시도 핸들러
     */
    const handleRetry = useCallback(() => {
        fetchSupplierInfo();
    }, [fetchSupplierInfo]);

    // ========================================
    // 6. Side Effects - 초기 데이터 로드
    // ========================================
    useEffect(() => {
        fetchSupplierInfo();
    }, [fetchSupplierInfo]);

    // 수정 여부를 부모 컴포넌트에 알림
    useEffect(() => {
        onUnsavedChanges?.(isEdited);
    }, [isEdited, onUnsavedChanges]);

    /**
     * 필드 변경 핸들러 (수정 상태 추적 포함)
     */
    const handleFieldChangeWithEdit = useCallback((field: keyof SupplierInfo | Partial<SupplierInfo>, value?: string) => {
        handleFieldChange(field, value);
        setIsEdited(true);
    }, [handleFieldChange]);

    /**
     * 저장 후 수정 상태 초기화
     */
    const handleSaveWithReset = useCallback(async (saveHandler: () => Promise<void>) => {
        await saveHandler();
        setIsEdited(false);
    }, []);


    // ========================================
    // 6. Render - 로딩 상태
    // ========================================
    if (loading) {
        return <Loading message="협력사 정보를 불러오는 중..." />;
    }

    // ========================================
    // 7. Render - 에러 상태
    // ========================================
    if (error || !supplierInfo) {
        return (
            <div className="supplier-detail-content">
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    backgroundColor: 'var(--color-white)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--border-radius-lg)',
                    padding: '40px'
                }}>
                    <i className="fas fa-exclamation-triangle" style={{
                        fontSize: '3rem',
                        color: 'var(--color-gray-400)',
                        marginBottom: '20px'
                    }}></i>
                    <p style={{
                        fontSize: '1.1rem',
                        color: 'var(--color-gray-600)',
                        marginBottom: '20px',
                        textAlign: 'center'
                    }}>
                        협력사 정보를 불러오는데 실패했습니다.<br />
                        네트워크 연결을 확인하고 다시 시도해주세요.
                    </p>
                    <button
                        className="search-btn"
                        onClick={handleRetry}
                        style={{ marginLeft: 0 }}
                    >
                        <i className="fas fa-redo"></i>
                        <span>다시 시도</span>
                    </button>
                </div>
            </div>
        );
    }

    // ========================================
    // 8. Render - 메인 UI
    // ========================================
    return (
        <div className="supplier-detail-content">
            {/* 페이지 헤더 */}
            <div className="page-header">
                {onBack && (
                    <button className="back-btn" type="button" onClick={onBack}>
                        <i className="fas fa-arrow-left" />
                        협력사 목록으로
                    </button>
                )}
                <h1 className="page-title">
                    협력사 상세
                    {supplierName && <span className="supplier-name-badge">{supplierName}</span>}
                </h1>
            </div>

            {/* 기본 정보 */}
            <div className="section-margin">
                <BasicInfoSection
                    supplierInfo={supplierInfo}
                    saving={saving}
                    onFieldChange={handleFieldChangeWithEdit}
                    onSave={() => handleSaveWithReset(handleSaveBasicInfo)}
                />
            </div>

            {/* 정산 정보 */}
            <div className="section-margin">
                <SettlementInfoSection
                    supplierInfo={supplierInfo}
                    saving={saving}
                    onFieldChange={handleFieldChangeWithEdit}
                    onSave={() => handleSaveWithReset(handleSaveSettlementInfo)}
                />
            </div>

            {/* 입금 정보 */}
            <div className="section-margin">
                <DepositInfoSection
                    supplierInfo={supplierInfo}
                />
            </div>

            {/* 사업자 정보 */}
            <div className="section-margin">
                <BusinessInfoSection
                    supplierInfo={supplierInfo}
                    saving={saving}
                    onFieldChange={handleFieldChangeWithEdit}
                    onSave={() => handleSaveWithReset(handleSaveBusinessInfo)}
                />
            </div>

            {/* 일차감 정보 */}
            <div className="section-margin">
                <DeductionSection
                    deductions={deductions}
                    currentDeductions={currentDeductions}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={goToPage}
                />
            </div>
        </div>
    );
};

export default SupplierDetailPage;
